/**
 * Efektivno radno vreme za jedan dan (bez pauza) — ista logika kao calculateWorkedMinutes na serveru (helpers.js).
 * Ulaz–izlaz parovi u nizu; pauza je između izlaza i sledećeg ulaza i ne ulazi u zbir.
 */

const timeToMinutes = (time) => {
    if (!time || typeof time !== "string") return 0
    const [hours, minutes] = time.split(":").map(Number)
    if (Number.isNaN(hours)) return 0
    return hours * 60 + (Number.isNaN(minutes) ? 0 : minutes)
}

/** Sortiranje logova jednog dana (vreme, pa stabilno po id). */
export const sortEmployeeDayLogs = (logs) =>
    [...logs].sort((a, b) => {
        const ta = timeToMinutes(a.vreme)
        const tb = timeToMinutes(b.vreme)
        if (ta !== tb) return ta - tb
        return String(a._id ?? "").localeCompare(String(b._id ?? ""))
    })

/** System-generated attendance types — days with only these logs do not get the calendar stripe. */
const SYSTEM_ATTENDANCE_TYPES = new Set([
    "Sistemski - Tag",
    "Automatska odjava",
    "Automatska odjava 12"
])

/** Returns true when the log's attendance type is system-generated (tag, auto logout, etc.). */
const isSystemAttendanceType = (tipPotvrda) => {
    const type = tipPotvrda && String(tipPotvrda).trim()
    return type && SYSTEM_ATTENDANCE_TYPES.has(type)
}

/** True when the day has at least one non-system attendance log — shows the white top stripe in the calendar. */
export const dayHasAttendanceMark = (dayLogs) => {
    if (!dayLogs || !dayLogs.length) return false
    return dayLogs.some((log) => !isSystemAttendanceType(log.tipPotvrda))
}

/** Cilj: 7.5h efektivno = tačan izlazak (u minutima). */
export const EMPLOYEE_DAY_TARGET_EFFECTIVE_MINUTES = 7.5 * 60

/** Koliko minuta oko 7.5h još tretiramo kao „tačno“ (zeleno). */
export const EMPLOYEE_DAY_EXACT_TOLERANCE_MINUTES = 2

/**
 * @param {object[]|undefined|null} dayLogs — logovi za jedan kalendarski dan (sortira se unutra)
 * @returns {number|null} null ako nema niza; 0 ako nema ulaza; inače zbir efektivnih minuta
 */
export const calculateEffectiveWorkedMinutes = (dayLogs) => {
    if (!dayLogs || !dayLogs.length) return null

    const userLogs = sortEmployeeDayLogs(dayLogs)
    const firstEntryIndex = userLogs.findIndex((log) => log.status === "ulaz")
    if (firstEntryIndex === -1) return 0

    let workedMinutes = 0

    for (let i = firstEntryIndex + 1; i < userLogs.length; i += 2) {
        const startWorkTime = userLogs[i - 1].vreme
        const endWorkTime = userLogs[i].vreme

        if (!endWorkTime) break

        const startWorkMin = timeToMinutes(startWorkTime)
        const endWorkMin = timeToMinutes(endWorkTime)

        if (userLogs[i].tipPotvrda === "Automatska odjava") {
            if (startWorkMin > endWorkMin) break
        }

        workedMinutes +=
            endWorkMin >= startWorkMin
                ? endWorkMin - startWorkMin
                : 1440 - startWorkMin + endWorkMin
    }

    return workedMinutes
}

const dayLogsKeyCandidates = (y, monthIndex0, date) => {
    const m1 = monthIndex0 + 1
    return [`${y}-${m1}-${date}`, `${y}-${String(m1).padStart(2, "0")}-${String(date).padStart(2, "0")}`]
}

export const getLogsForCalendarDay = (logsByDay, y, monthIndex0, date) => {
    if (!logsByDay || typeof logsByDay !== "object") return []
    for (const key of dayLogsKeyCandidates(y, monthIndex0, date)) {
        const arr = logsByDay[key]
        if (Array.isArray(arr) && arr.length) return arr
    }
    return []
}

/**
 * @returns {"neutral"|"absent"|"exact"|"over"|"short"}
 */
export const getEmployeeDayCalendarTone = (dayLogs) => {
    const worked = calculateEffectiveWorkedMinutes(dayLogs)
    if (worked === null) return "absent"

    const hasUlaz = dayLogs.some((l) => l.status === "ulaz")
    if (!hasUlaz) return "absent"

    const t = EMPLOYEE_DAY_TARGET_EFFECTIVE_MINUTES
    const tol = EMPLOYEE_DAY_EXACT_TOLERANCE_MINUTES
    if (Math.abs(worked - t) <= tol) return "exact"
    if (worked > t + tol) return "over"
    return "short"
}
